import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, UserMinus, Loader2, CalendarOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { value: "cuti",   label: "Cuti",    color: "bg-red-600 text-white border-red-700" },
  { value: "sakit",  label: "Sakit",   color: "bg-orange-600 text-white border-orange-700" },
  { value: "izin",   label: "Izin",    color: "bg-amber-600 text-white border-amber-700" },
  { value: "break",  label: "Break",   color: "bg-purple-600 text-white border-purple-700" },
  { value: "lainnya",label: "Lainnya", color: "bg-zinc-600 text-white border-zinc-700" },
];
function reasonMeta(r) { return REASONS.find(x=>x.value===r) || REASONS[REASONS.length-1]; }

/**
 * v84.49 — Break/Cuti Bar (top of CalendarBoard)
 * Menampilkan sampai 3 chip personel yang sedang break/cuti untuk tanggal aktif.
 * Chip 4-N tetap tersimpan tapi ditampilkan sebagai "+N lainnya".
 *
 * Props:
 *  - date       : "YYYY-MM-DD"
 *  - personnel  : Personnel[] (parent-cached)
 *  - reloadKey  : number
 *  - onChanged  : callback
 */
export function BreakLeaveBar({ date, personnel = [], reloadKey = 0, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pickReason, setPickReason] = useState("cuti");

  const personnelMap = useMemo(() => {
    const m = {}; for (const p of personnel) m[p.id] = p; return m;
  }, [personnel]);
  const takenIds = useMemo(() => new Set(rows.map(r => r.personnel_id)), [rows]);
  const available = personnel.filter(p => p.active !== false && !takenIds.has(p.id));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listBreakLeaves({ date });
        if (!cancelled) setRows(r.rows || []);
      } catch (e) { console.error("[BreakLeaveBar] load fail", e); }
    })();
    return () => { cancelled = true; };
  }, [date, reloadKey]);

  async function addRow(pid) {
    setBusy(true);
    try {
      const r = await api.createBreakLeave({ date, personnel_id: pid, reason: pickReason });
      if (!r || !r.id) {
        toast.error("Response server tidak lengkap. Coba refresh & cek lagi.");
        return;
      }
      setRows(prev => {
        // Replace if same personnel_id else append
        const filtered = prev.filter(x => x.personnel_id !== pid);
        return [...filtered, r];
      });
      setAddOpen(false);
      // v84.90 — Toast konfirmasi eksplisit supaya admin yakin tersimpan di DB
      const personName = personnel.find(p => p.id === pid)?.name || "personel";
      toast.success(`${personName} tercatat ${pickReason} pada ${date}`);
      onChanged?.();
    } catch (e) {
      toast.error("Gagal tambah: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  async function removeRow(id) {
    // v84.90 — Confirm dialog utk hindari accidental delete
    const row = rows.find(r => r.id === id);
    const pname = row ? (personnelMap[row.personnel_id]?.name || "personel") : "record ini";
    if (!window.confirm(`Hapus tag ${row?.reason || "break"} untuk ${pname} tgl ${date}?\n\nCatatan: saldo cuti akan dikembalikan otomatis.`)) return;
    setBusy(true);
    try {
      await api.deleteBreakLeave(id);
      setRows(prev => prev.filter(x => x.id !== id));
      toast.success(`Tag ${row?.reason || "break"} ${pname} dihapus`);
      onChanged?.();
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  const visible = rows.slice(0, 3);
  const overflow = rows.length - visible.length;

  return (
    <div className="flex items-center gap-1.5 min-w-0" data-testid="break-leave-bar">
      <div className="flex items-center gap-1 text-[10px] text-zinc-500 uppercase tracking-widest font-mono flex-shrink-0">
        <CalendarOff className="w-3 h-3" />
        <span className="hidden md:inline">Cuti</span>
      </div>
      <div className="flex items-center gap-1 flex-wrap max-w-[420px]">
        {visible.length === 0 && !addOpen && (
          <span className="text-[10px] text-zinc-600 italic">Semua tim available</span>
        )}
        {visible.map(row => {
          const p = personnelMap[row.personnel_id];
          const meta = reasonMeta(row.reason);
          return (
            <div key={row.id}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium border ${meta.color} group`}
              data-testid={`break-leave-chip-${row.id}`}
              title={`${p?.name || row.personnel_id} — ${meta.label}${row.note ? " · " + row.note : ""}`}>
              <span className="truncate max-w-[110px]">{p?.name || "?"}</span>
              <span className="text-[8px] font-mono opacity-70 uppercase">{meta.label}</span>
              <button onClick={() => removeRow(row.id)} disabled={busy}
                className="opacity-0 group-hover:opacity-100 hover:text-yellow-200 transition-opacity"
                data-testid={`break-leave-remove-${row.id}`}>
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          );
        })}
        {overflow > 0 && (
          <span className="text-[10px] text-zinc-500 font-mono">+{overflow}</span>
        )}
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] text-zinc-500 border border-dashed border-border hover:border-red-500/50 hover:text-red-400 transition-colors"
              data-testid="break-leave-add-btn"
              disabled={busy}>
              {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <UserMinus className="w-2.5 h-2.5" />}
              <span>Tambah</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="end" data-testid="break-leave-popover">
            <div className="p-2 border-b border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Alasan</div>
              <div className="flex flex-wrap gap-1">
                {REASONS.map(r => (
                  <button key={r.value}
                    onClick={()=>setPickReason(r.value)}
                    className={`px-2 py-0.5 rounded-sm text-[10px] border ${pickReason===r.value ? r.color + " font-semibold" : "border-border text-zinc-500 hover:border-zinc-400"}`}
                    data-testid={`break-leave-reason-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-64 overflow-auto">
              <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Pilih Personel</div>
              {available.length === 0 && (
                <div className="p-3 text-[11px] text-muted-foreground">Semua personel sudah ter-tag.</div>
              )}
              {available.map(p => (
                <button key={p.id}
                  onClick={()=>addRow(p.id)}
                  className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-muted flex items-center justify-between gap-2 border-b border-border last:border-b-0"
                  data-testid={`break-leave-pick-${p.id}`}>
                  <span className="truncate">{p.name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{p.commission_percentage}%</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
